import { useState } from 'react'
import { AccountRecord, useUpdateRetirePostDaysMutation } from '../../api/client'
import Button from '../Button'
import { toastError, toastSuccess } from '../Notification'
import { RETIRE_POST_DAYS_MAX, RETIRE_POST_DAYS_MIN, isValidRetirePostDays } from './retirePostDays'

type Props = {
    account: AccountRecord
    /** What the setting limits on this account's platform, from `retirePostDaysExplanation`. */
    explanation: string
}

/** How many days after publishing a post stops being fetched or scraped, with what that limits here. */
const RetirePostDaysSetting = ({ account, explanation }: Props) => {
    const [updateRetirePostDays, { loading }] = useUpdateRetirePostDaysMutation()
    const [saved, setSaved] = useState(account.retirePostDays)
    const [draft, setDraft] = useState(String(account.retirePostDays))

    const days = Number(draft)
    const valid = draft.trim() !== '' && isValidRetirePostDays(days)
    const changed = valid && days !== saved

    const onSave = async () => {
        try {
            const result = await updateRetirePostDays({
                variables: { accountId: account.id, retirePostDays: days }
            })
            const stored = result.data?.updateRetirePostDays.retirePostDays ?? days
            setSaved(stored)
            setDraft(String(stored))
            toastSuccess(`Posts now stop being collected after ${stored} days.`)
        } catch (err: any) {
            console.error(err)
            toastError(err.message)
        }
    }

    const inputId = `retire-post-days-${account.id}`

    return (
        <div className="flex flex-col gap-2">
            <label htmlFor={inputId} className="text-sm font-semibold text-ink-900">
                Stop collecting a post after (days)
            </label>
            <div className="flex items-center gap-3">
                <input
                    id={inputId}
                    type="number"
                    inputMode="numeric"
                    min={RETIRE_POST_DAYS_MIN}
                    max={RETIRE_POST_DAYS_MAX}
                    step={1}
                    value={draft}
                    onChange={(event) => setDraft(event.target.value)}
                    aria-invalid={!valid}
                    className="w-24 rounded-md border border-ink-200 px-2 py-1.5 text-sm text-ink-900 shadow-sm focus:border-arwen-500 focus:ring-arwen-500"
                />
                <Button
                    type="button"
                    primary={true}
                    size="small"
                    text="Save"
                    loading={loading}
                    disabled={!changed || loading}
                    onClick={onSave}
                />
            </div>
            {!valid && (
                <p className="text-sm text-red-600">
                    Enter a whole number of days from {RETIRE_POST_DAYS_MIN} to{' '}
                    {RETIRE_POST_DAYS_MAX}.
                </p>
            )}
            <p className="text-sm text-ink-600">{explanation}</p>
        </div>
    )
}

export default RetirePostDaysSetting
